/**
 * sitzung.ts — Merker für die Anmeldeseite über einen Seitenwechsel hinweg.
 *
 * 1. „Sitzung abgelaufen" (08.10.2026): Wird der Erneuerungs-Schlüssel
 *    (Refresh Token) ungültig, meldet Supabase `SIGNED_OUT`. Bis hier
 *    ignorierte die App das: Der Nutzer blieb auf dem Bildschirm sichtbar,
 *    jede Aktion scheiterte aber still an der Datenbank-Berechtigung (Toast
 *    „42501"). Jetzt lädt `useAuth` in diesem Fall neu, und die Anmeldeseite
 *    zeigt den Hinweis „Deine Sitzung ist abgelaufen". Der Merker überbrückt
 *    den Neuladen.
 * 2. „Passwort-Link ungültig" (12.10.2026): Scheitert auf `/recovery` das
 *    Einlösen des Links aus der Mail (abgelaufen oder schon benutzt), öffnet
 *    die Anmeldeseite gleich „Passwort vergessen" mit einem Hinweis, statt
 *    stumm die Startseite zu zeigen.
 *
 * `sessionStorage` statt `localStorage`: gilt nur für diesen Tab und
 * verschwindet mit ihm — ein später geöffneter Tab soll den Hinweis nicht
 * mehr zeigen. Zugriffe in try/catch, weil Speicher gesperrt sein kann
 * (manche Inkognito-Modi).
 */

const SCHLUESSEL_SITZUNG = 'drpx_sitzung_abgelaufen';
const SCHLUESSEL_PW_LINK = 'drpx_pw_link_ungueltig';

/** Setzt einen Merker; bei gesperrtem Speicher eben ohne Hinweis. */
function setzen(schluessel: string): void {
  try {
    sessionStorage.setItem(schluessel, '1');
  } catch (_e: unknown) {
    /* Speicher gesperrt — dann eben ohne Hinweis */
  }
}

/** Schaut nach, ob ein Merker gesetzt ist, ohne ihn zu löschen. */
function pruefen(schluessel: string): boolean {
  try {
    return sessionStorage.getItem(schluessel) === '1';
  } catch (_e: unknown) {
    return false;
  }
}

/** Liest einen Merker und löscht ihn sofort (Hinweis erscheint nur einmal). */
function holen(schluessel: string): boolean {
  try {
    const war = sessionStorage.getItem(schluessel) === '1';
    if (war) sessionStorage.removeItem(schluessel);
    return war;
  } catch (_e: unknown) {
    return false;
  }
}

/** Merkt sich für den nächsten Seitenaufbau: Sitzung ist abgelaufen. */
export function merkeSitzungAbgelaufen(): void {
  setzen(SCHLUESSEL_SITZUNG);
}

/**
 * Schaut nur nach, ob der Merker gesetzt ist, ohne ihn zu löschen.
 * `AuthScreen` öffnet damit gleich das Anmeldeformular statt der Startseite;
 * das Formular selbst liest und löscht den Merker dann mit
 * {@link holeSitzungAbgelaufen}.
 *
 * @returns true, wenn die Sitzung abgelaufen war
 */
export function istSitzungAbgelaufenGemerkt(): boolean {
  return pruefen(SCHLUESSEL_SITZUNG);
}

/**
 * Liest den Merker und löscht ihn sofort (der Hinweis erscheint nur einmal).
 *
 * @returns true, wenn die Sitzung abgelaufen war
 */
export function holeSitzungAbgelaufen(): boolean {
  return holen(SCHLUESSEL_SITZUNG);
}

/** Merkt sich: Der Passwort-Link aus der Mail ließ sich nicht einlösen. */
export function merkePwLinkUngueltig(): void {
  setzen(SCHLUESSEL_PW_LINK);
}

/**
 * Schaut nur nach, ob „Passwort-Link ungültig" gemerkt ist (für
 * `AuthScreen`: gleich das Formular statt der Startseite zeigen).
 *
 * @returns true, wenn der Link ungültig war
 */
export function istPwLinkUngueltigGemerkt(): boolean {
  return pruefen(SCHLUESSEL_PW_LINK);
}

/**
 * Liest „Passwort-Link ungültig" und löscht den Merker sofort.
 *
 * @returns true, wenn der Link ungültig war
 */
export function holePwLinkUngueltig(): boolean {
  return holen(SCHLUESSEL_PW_LINK);
}

const SCHLUESSEL_ZUGANG = 'drpx_zugang_inaktiv';

/**
 * Merkt sich: Ein Mitarbeiter-Zugang hat sich angemeldet, ist aber nicht
 * (mehr) aktiv — eingeladen, gesperrt, Firma gelöscht (DRPX-Pro, 16.10.2026).
 * `useAuth` meldet ihn dann ab und lädt neu; die Anmeldeseite zeigt den
 * Hinweis „Dein Zugang ist nicht aktiv".
 */
export function merkeZugangInaktiv(): void {
  setzen(SCHLUESSEL_ZUGANG);
}

/**
 * Schaut nur nach, ob „Zugang nicht aktiv" gemerkt ist (für `AuthScreen`:
 * gleich das Formular statt der Startseite zeigen).
 *
 * @returns true, wenn der Zugang nicht aktiv war
 */
export function istZugangInaktivGemerkt(): boolean {
  return pruefen(SCHLUESSEL_ZUGANG);
}

/**
 * Liest „Zugang nicht aktiv" und löscht den Merker sofort.
 *
 * @returns true, wenn der Zugang nicht aktiv war
 */
export function holeZugangInaktiv(): boolean {
  return holen(SCHLUESSEL_ZUGANG);
}
